/// <amd-module name="Dashboards/DashboardView/src/redux/actions/TelemetryActions" />
import { Action, ActionsUnion, createAction, trackedAction } from "redux/Utils/ActionHelper";
import { Dashboard } from "../../model/Dashboard";
import * as TelemetrySelectors from "../selectors/TelemetrySelectors";

import { TelemetryActions as VssTelemetryActions } from "VSS/Redux/Modules/Telemetry";
import { TTIMonitorActions } from "VSS/Redux/Modules/TTIMonitor";

export const FeatureArea = "Dashboards";

const TTIScenarioName = "Dashboards.Page.Load";
export const TTISplitTimingName = "DashboardView.Render";

/**
 * This scenario is published instead of the normal TTI scenario.
 * Indicates TTI would have been unfairly impacted by actions the user
 * took on the page such as scrolling new widgets into the viewport before tt
 * triggering them to load.
 */
export const OmittedTTIScenarioName = "Dashboard.TTI.Omitted";

export const enum TelemetryActionTypes {
    PublishDashboardComposition = "Dashboard.Load",

    PublishAutoRefreshCycleTelemetry = "Dashboard.AutoRefreshCycle",
    PublishRefreshTelemetry = "Dashboard.Refresh",

    PublishOnConfigureTelemetry = "Dashboard.ConfigureWidget",
    PublishOnCopyWidgetToDashboardTelemetry = "Dashboard.CopyWidgetToDashboard",
    PublishOnAddFromCatalogTelemetry = "Dashboard.AddFromCatalog",
    PublishOnDeleteTelemetry = "Dashboard.DeleteWidget",
    
    PublishOnCopyDashboardSuccessTelemetry = "Dashboard.CopyDashboardSuccess",
    PublishOnCopyDashboardTelemetry = "Dashboard.CopyDashboard",
    PublishOnDashboardEditMode = 'Dashboard.EditMode',
    PublishOnDashboardDoneEditing = 'Dashboard.DoneEditing'
}

/* Notes - The trackedActions() used with this namespace automatically get handled by a common saga path, which takes responsibility for publishing these events.
 * Telemetry has a long standing convention of contributionId expressed under WidgetTypeId. */
export namespace TelemetryActions {
    export const markScenario = (scenarioName: string, properties: any, isPageInteractive?: boolean) =>
        VssTelemetryActions.endScenario(scenarioName, properties, isPageInteractive);
    export const markTTI = (properties: TelemetrySelectors.DashboardRuntimeCompositionProperties) =>
        TTIMonitorActions.markTTI(FeatureArea, TTIScenarioName, properties);

    export const publishDashboardComposition = (properties: TelemetrySelectors.DashboardCompositionProperties) =>
        trackedAction(createAction(TelemetryActionTypes.PublishDashboardComposition), properties);

    export const publishAutoRefreshCycleTelemetry: (properties: {
        [key: string]: string | number;
    }) => Action<TelemetryActionTypes.PublishAutoRefreshCycleTelemetry> = (properties: { [key: string]: string | number }) =>
        trackedAction(createAction(TelemetryActionTypes.PublishAutoRefreshCycleTelemetry), properties); // Note: This fires even if page is throttled, and actual refresh operation is not run
    export const publishOnRefresh = (dashboard: Dashboard, isAutoRefresh: boolean, isHardRefresh: boolean) =>
        trackedAction(createAction(TelemetryActionTypes.PublishRefreshTelemetry), { isAutoRefresh, dashboardId: dashboard.id, isHardRefresh });
    export const publishOnCopyDashboardSuccess = (properties: TelemetrySelectors.CopyDashboardSuccessProperties) =>
        trackedAction(createAction(TelemetryActionTypes.PublishOnCopyDashboardSuccessTelemetry), properties);
    export const publishOnCopyDashboard = (properties: TelemetrySelectors.CopyDashboardProperties) =>
    trackedAction(createAction(TelemetryActionTypes.PublishOnCopyDashboardTelemetry), properties);

    // Widget (Host) scoped Events
    export const publishOnCopyToDashboard = (properties: TelemetrySelectors.CopyToDashboardProperties) =>
        trackedAction(createAction(TelemetryActionTypes.PublishOnCopyWidgetToDashboardTelemetry), properties);

    export const publishOnConfigureWidget = (properties: TelemetrySelectors.BasicWidgetProperties) =>
        trackedAction(createAction(TelemetryActionTypes.PublishOnConfigureTelemetry), properties);
    export const publishOnDeleteWidget = (properties: TelemetrySelectors.BasicWidgetProperties) =>
        trackedAction(createAction(TelemetryActionTypes.PublishOnDeleteTelemetry), properties);
    export const publishOnAddFromCatalog = (properties: TelemetrySelectors.AddFromCatalogWidgetProperties) =>
        trackedAction(createAction(TelemetryActionTypes.PublishOnAddFromCatalogTelemetry), properties);
    export const publishOnDashboardEditMode = (properties: TelemetrySelectors.IDashboardEditModeProperties) =>
        trackedAction(createAction(TelemetryActionTypes.PublishOnDashboardEditMode), properties);
    export const publishOnDashboardDoneEditing = (properties: TelemetrySelectors.IDashboardEditModeProperties) =>
        trackedAction(createAction(TelemetryActionTypes.PublishOnDashboardDoneEditing), properties);
}

export type TelemetryAction = ActionsUnion<typeof TelemetryActions>;
